import { useAuth0, User } from "@auth0/auth0-react";
import {
  AccountCircleOutlined,
  BarChartOutlined as AnalyticsIcon,
  GroupOutlined as GroupsIcon,
  HomeOutlined as HomeIcon,
  LogoutOutlined as LogoutIcon,
  OpenInNewOutlined as OpenInNewIcon,
  PersonOutlined as PersonIcon,
} from "@mui/icons-material";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Toolbar,
  useTheme,
} from "@mui/material";
import { AuthorizationResources } from "@rw1/permissions";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppConfig } from "rwo-react";
import { useCompanySettings } from "../hooks";
import { AreaAccessMap, useCheckAreaAccess } from "../hooks/useCheckAreaAccess";
import { useCompaniesData } from "../hooks/useCompaniesData";
import { useCurrentUserRoles } from "../hooks/useCurrentUserRoles";
import { useIsMobile } from "../hooks/useIsMobile";
import { useLocalization } from "../hooks/useLocalization";
import { useLogout } from "../hooks/useLogout";
import { Rw1Logo } from "../icons/Rw1Logo";
import { Routes } from "../types/NavigationPaths";
import { ExperiencesIcon } from "./ExperiencesIcon";
import { LicensesIcon } from "./LicensesIcon";
import { PopoverMenu, PopoverMenuItem } from "./PopoverMenu";

const drawerWidth = 210;

const COMPANY_SELECTOR_FEATURE_FLAG = "uam_showCompanySelector_For_Administrators";

interface NavigationItem {
  text: string;
  path: Routes;
  icon: React.ReactElement;
}

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

interface UseNavigationItemsOptions {
  isDataAnalyticsEnabled: boolean;
  access: AreaAccessMap;
}

const useNavigationItems = ({
  isDataAnalyticsEnabled,
  access,
}: UseNavigationItemsOptions): NavigationItem[] => {
  const { t } = useLocalization();

  return useMemo(() => {
    const items: NavigationItem[] = [];

    if (access.company) {
      items.push({
        text: t("components.sidebar.myCompany"),
        path: Routes.COMPANY,
        icon: <HomeIcon fontSize="small" />,
      });
    }

    if (access.uam) {
      items.push(
        {
          text: t("components.sidebar.users"),
          path: Routes.USERS,
          icon: <PersonIcon fontSize="small" />,
        },
        {
          text: t("components.sidebar.groups"),
          path: Routes.GROUPS,
          icon: <GroupsIcon fontSize="small" />,
        },
        {
          text: t("components.sidebar.experiences"),
          path: Routes.EXPERIENCES,
          icon: <ExperiencesIcon fontSize="small" />,
        }
      );
    }

    if (access.license) {
      items.push({
        text: t("components.sidebar.externalLicenses"),
        path: Routes.EXTERNAL_LICENSES,
        icon: <LicensesIcon fontSize="small" />,
      });
    }

    if (isDataAnalyticsEnabled && access.insights) {
      items.push({
        text: t("components.sidebar.dataAnalytics"),
        path: Routes.INSIGHTS,
        icon: <AnalyticsIcon fontSize="small" />,
      });
    }

    return items;
  }, [access, isDataAnalyticsEnabled, t]);
};

export const Sidebar: React.FC<SidebarProps> = ({
  mobileOpen,
  onMobileClose,
}) => {
  const queryClient = useQueryClient();
  const { featureFlags } = useAppConfig();
  const showCompanySelector = useMemo(
    () => Boolean(featureFlags[COMPANY_SELECTOR_FEATURE_FLAG]),
    [featureFlags],
  );

  const { userRoles } = useCurrentUserRoles();

  // Only enable fetching company dropdown data if feature flag is active
  const { companiesData } = useCompaniesData({ enabled: showCompanySelector });

  const accessibleCompanies = useMemo(() => {
    return companiesData
      ?.filter((x) =>
        userRoles?.some(
          (r) =>
            r.roleId === AuthorizationResources.Global.Group.Administrator ||
            (r.organizationId === x.companyId && r.roleId === AuthorizationResources.Global.Group.Company_Administrator)
        )
      )
      .sort((a, b) => (a.friendlyName).localeCompare(b.friendlyName));
  }, [companiesData, userRoles]);

  const { t } = useLocalization();
  const { user, isAuthenticated } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const handleLogout = useLogout();

  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const { companyId } = useParams();
  const { isDataAnalyticsEnabled, isLoading: isCompanySettingsLoading } = useCompanySettings({ companyId });

  const { access, isLoading: isAccessLoading } = useCheckAreaAccess(companyId);

  const navigationItems = useNavigationItems({
    isDataAnalyticsEnabled: !!isDataAnalyticsEnabled,
    access,
  });

  const buildPath = (path: Routes, targetCompanyId?: string): string => {
    const activeCompanyId = targetCompanyId ?? companyId;
    const base = activeCompanyId ? `/companies/${activeCompanyId}` : "";

    return path === Routes.COMPANY ? base || "/" : `${base}${path}`;
  };
  const isNavLoading = isAccessLoading || isCompanySettingsLoading;

  useEffect(() => {
    if (isNavLoading || isLoggingOut || !isAuthenticated) {
      return;
    }

    if (navigationItems.length > 0) {
      const firstPath = buildPath(navigationItems[0]!.path);
      const isBaseCompanyRoute = companyId && location.pathname === `/companies/${companyId}`;
      const isRootRoute = location.pathname === "/";

      if (isRootRoute || isBaseCompanyRoute) {
        void navigate(firstPath, { replace: true });
      }
    }
  }, [navigationItems, isNavLoading, isLoggingOut, isAuthenticated, location.pathname, companyId, navigate]);

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const menuItems: PopoverMenuItem[] = [
    {
      label: t("components.sidebar.imprint"),
      href: "https://realworld-one.com/imprint",
      icon: <OpenInNewIcon fontSize="small" />,
    },
    {
      label: t("components.sidebar.privacyNotice"),
      href: "https://realworld-one.com/privacy-notice",
      icon: <OpenInNewIcon fontSize="small" />,
    },
    {
      label: t("components.sidebar.supportPortal"),
      href: "https://support.realworld-one.com/",
      icon: <OpenInNewIcon fontSize="small" />,
    },
    { type: "divider" },
    {
      label: t("components.sidebar.logOut"),
      onClick: () => {
        setIsLoggingOut(true);
        handleClose();
        handleLogout();
      },
      icon: <LogoutIcon fontSize="small" />,
    },
  ];

  const isPathSelected = (itemPath: Routes) => {
    const fullPath = buildPath(itemPath);
    if (itemPath === Routes.COMPANY) {
      return location.pathname === fullPath;
    }

    return location.pathname.startsWith(fullPath);
  };

  const handleNavigation = (path: Routes) => {
    void navigate(buildPath(path));

    if (isMobile) {
      onMobileClose();
    }
  };

  const handleCompanyChange = (newCompanyId: string) => {
    queryClient.clear();
    void navigate(`/companies/${newCompanyId}`);
  };

  const drawerContent = (
    <>
      <Toolbar>
        <Rw1Logo height="40px" />
      </Toolbar>
      {showCompanySelector && (accessibleCompanies?.length ?? 0) > 1 && (
        <Box sx={{ px: 2, py: 1 }}>
          <Select
            displayEmpty
            fullWidth
            onChange={(e) => handleCompanyChange(e.target.value)}
            size="small"
            value={companyId ?? ""}
          >
            {accessibleCompanies?.map((company) => (
              <MenuItem key={company.companyId} value={company.companyId}>
                {company.friendlyName}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}
      <Box
        sx={{
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          paddingTop: "20px",
          mb: 2,
        }}
      >
        <List>
          {navigationItems.map((item) => (
            <MenuItemButton
              icon={item.icon}
              key={item.text}
              onClick={() => handleNavigation(item.path)}
              selected={isPathSelected(item.path)}
              text={item.text}
            />
          ))}
        </List>
        <Box sx={{ flexGrow: 1 }} />
        {user && (
          <MenuItemButton
            icon={<AccountCircleOutlined fontSize="small" />}
            onClick={handleProfileClick}
            selected={open}
            text={getUserDisplayName(user)}
          />
        )}
        <PopoverMenu
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          items={menuItems}
          onClose={handleClose}
          open={open}
          slotProps={{
            paper: {
              sx: {
                mt: 0,
                mb: 1.5,
              },
            },
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        />
      </Box>
    </>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { md: drawerWidth },
        flexShrink: { md: 0 },
      }}
    >
      <Drawer
        ModalProps={{
          keepMounted: true,
        }}
        onClose={onMobileClose}
        open={mobileOpen}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            backgroundColor: theme.colors.secondary[0],
          },
        }}
        variant="temporary"
      >
        {drawerContent}
      </Drawer>

      <Drawer
        open
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            backgroundColor: theme.colors.secondary[0],
            boxShadow: "none",
            border: "none",
          },
        }}
        variant="permanent"
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};

interface MenuItemButtonProps {
  text: string;
  icon: React.ReactElement;
  onClick: React.MouseEventHandler<HTMLDivElement> | undefined;
  selected?: boolean;
}

const MenuItemButton: React.FC<MenuItemButtonProps> = ({
  text,
  icon,
  onClick,
  selected = false,
}) => {
  const theme = useTheme();

  return (
    <ListItem disablePadding>
      <ListItemButton
        onClick={onClick}
        selected={selected}
        sx={{
          color: theme.colors.text[1],
          paddingX: 3,
          height: 44,
          "&:hover": {
            backgroundColor: "inherit",
            color: theme.colors.primary[5],
            "& .MuiListItemIcon-root": {
              color: theme.colors.primary[5],
            },
          },
          "&.Mui-selected": {
            backgroundColor: "inherit",
            color: theme.colors.primary[5],
            "&:hover": {
              backgroundColor: "inherit",
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            marginRight: 1,
            color: selected ? theme.colors.primary[5] : theme.colors.text[1],
          }}
        >
          {icon}
        </ListItemIcon>
        <ListItemText
          primary={text}
          sx={{
            "& .MuiListItemText-primary": {
              fontWeight: 600,
              fontSize: "14px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }}
        />
      </ListItemButton>
    </ListItem>
  );
};

const getUserDisplayName = (user: User) => {
  const customUser = user as User & {
    "http://realworld-one.com/forename"?: string;
    "http://realworld-one.com/surname"?: string;
  };

  const forename = customUser["http://realworld-one.com/forename"];
  const surname = customUser["http://realworld-one.com/surname"];

  if (forename && surname) {
    return `${surname}, ${forename}`;
  }

  if (user.family_name && user.given_name) {
    return `${user.family_name}, ${user.given_name}`;
  }

  return user.email ?? "Unknown User";
};
